/* Real implementation of InsightClient against the gateway insights read API
 * (/insights). Like the settings clients it gets FastAPI's `{ error: "<msg>" }`
 * / `{ detail }` error shapes; we normalize failures to the contract
 * `{ error: { code, message } }` envelope so the shared friendlyError()/
 * toApiError() helpers work uniformly. */

import { authFetchInit } from "../auth/fetch";
import { workspaceHeader } from "../identity/active";
import type { InsightClient } from "./InsightClient";
import type { FeedbackVerdict, Insight } from "./types";
import type { ApiError, ApiErrorCode } from "../analytics";

function codeForStatus(status: number): ApiErrorCode {
  if (status === 401) return "unauthorized";

  if (status === 403) return "forbidden";

  if (status === 404) return "not_found";

  if (status === 400 || status === 409 || status === 422) return "bad_request";

  return "upstream";
}

/** Turn an insights error body into a human message. The gateway returns
 *  `{ error: "<msg>" }` for business errors (404/409) and FastAPI's
 *  `{ detail: ... }` (string, or a list of {loc,msg}) for request-schema
 *  validation — handle both. */
function messageFromDetail(body: unknown, status: number): string {
  const err = (body as { error?: unknown })?.error;
  if (typeof err === "string") return err;

  const detail = (body as { detail?: unknown })?.detail;
  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => (d as { msg?: string }).msg).filter(Boolean);
    if (msgs.length) return msgs.join("; ");
  }

  return `Request failed (HTTP ${status}).`;
}

export class HttpInsightClient implements InsightClient {
  private readonly baseUrl: string;
  private readonly apiKey?: string;

  /** @param baseUrl gateway insights base, e.g. "http://host:8081/insights". */
  constructor(baseUrl: string, apiKey?: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
    this.apiKey = apiKey;
  }

  private headers(): Record<string, string> {
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
      ...workspaceHeader(),
    };
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}${path}`, {
        ...authFetchInit(),
        ...init,
        headers: this.headers(),
      });
    } catch (cause) {
      const error: ApiError = {
        code: "upstream",
        message: "Couldn't reach the insights service.",
        detail: String(cause),
      };
      throw { error };
    }
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const error: ApiError = {
        code: codeForStatus(res.status),
        message: messageFromDetail(body, res.status),
        detail: body,
      };
      throw { error };
    }

    return (await res.json()) as T;
  }

  async list(agentId: string): Promise<Insight[]> {
    return (
      (await this.request<Insight[]>(
        `?agent_id=${encodeURIComponent(agentId)}`,
      )) ?? []
    );
  }

  async listAll(): Promise<Insight[]> {
    return (await this.request<Insight[]>("/all")) ?? [];
  }

  async apply(insightId: string): Promise<Insight> {
    return this.request<Insight>(`/${encodeURIComponent(insightId)}/apply`, {
      method: "POST",
    });
  }

  async dismiss(insightId: string): Promise<Insight> {
    return this.request<Insight>(`/${encodeURIComponent(insightId)}/dismiss`, {
      method: "POST",
    });
  }

  async acknowledge(insightId: string): Promise<Insight> {
    return this.request<Insight>(
      `/${encodeURIComponent(insightId)}/acknowledge`,
      { method: "POST" },
    );
  }

  /** Omits `comment` when empty rather than sending "". The BFF is last-write-
   *  wins on BOTH fields, so omitting a comment CLEARS the stored one — callers
   *  that want to keep it must resend it with every verdict. */
  async submitFeedback(
    insightId: string,
    verdict: FeedbackVerdict,
    comment?: string,
  ): Promise<Insight> {
    return this.request<Insight>(`/${encodeURIComponent(insightId)}/feedback`, {
      method: "POST",
      body: JSON.stringify({ verdict, ...(comment ? { comment } : {}) }),
    });
  }
}
